import type { ConditionKey, Effect, Operator, ResourceManagementType } from '@libs/enums';
import type { ItemAttributes, ItemNames, OrEmpty, WithBlankSearchObject } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  ItemName,
  Locale,
  SingleValue,
  ValueSet,
  WithBlankSearch,
} from '@ps-smart-cp/ppfw-client';

interface Action {
  action: string;
  name: string;
}

interface PolicyMst {
  svc: OrEmpty<string>;
  effect: Effect;
  actions: Set<string>;
  key: OrEmpty<ConditionKey>;
  operator: OrEmpty<Operator>;
  isAllActions: boolean;
  allActions: Action[];
  svcKey: string;
  /** 条件値 */
  value: string;
  type: ResourceManagementType;
  typeCondition: Set<ResourceManagementType>;
  noteCondition: WithBlankSearchObject;
  conditionKeys: Set<ConditionKey>;
}

interface Schema {
  action: Action;
  policyMst: PolicyMst;
}

interface Record {
  svc: string;
  effect: string;
  actions: string[];
  allActions: { action: string; name: string }[];
  key: string;
  svcKey: string;
  operator: string;
  type: string[];
  isManaged: boolean;
  conditionKeys: string[];
}

const attributes = {
  svc: SingleValue.shortValue(true),
  effect: SingleValue.shortValue(),
  actions: ValueSet.new(true),
  key: SingleValue.shortValue(),
  operator: SingleValue.shortValue(),
  isAllActions: Bool.new(),
  type: ValueSet.new(),
  typeCondition: SingleValue.longValue(),
  noteCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 280 })),
} satisfies ItemAttributes<Omit<Schema['policyMst'], 'allActions' | 'conditionKeys' | 'svcKey' | 'value'>>;

const titleNames = {
  maintenance: ItemName.of(
    [Locale.ja, 'ポリシーマスタメンテナンス'],
    [Locale.en, 'Policy Master Maintenance'],
  ),
  register: ItemName.of([Locale.ja, 'ポリシーマスタ登録'], [Locale.en, 'Policy Master Register']),
  change: ItemName.of([Locale.ja, 'ポリシーマスタ変更'], [Locale.en, 'Policy Master Change']),
  detail: ItemName.of([Locale.ja, 'ポリシー詳細'], [Locale.en, 'Policy Detail']),
  policyList: ItemName.of([Locale.ja, 'ポリシー一覧'], [Locale.en, 'Policy List']),
};

const names = {
  svc: ItemName.of([Locale.ja, 'サービス'], [Locale.en, 'Service']),
  effect: ItemName.of([Locale.ja, 'アクション許可 '], [Locale.en, 'Effect']),
  actions: ItemName.of([Locale.ja, 'アクション'], [Locale.en, 'Actions']),
  key: ItemName.of([Locale.ja, '条件キー'], [Locale.en, 'Condition Key']),
  operator: ItemName.of([Locale.ja, '演算子'], [Locale.en, 'Operator']),
  allActions: ItemName.of([Locale.ja, 'すべてのアクション'], [Locale.en, 'All Actions']),
  value: ItemName.of([Locale.ja, '値'], [Locale.en, 'Value']),
  type: ItemName.of([Locale.ja, 'タイプ'], [Locale.en, 'Type']),
} satisfies ItemNames<Schema['policyMst']>;

const permissionNames = {
  selectSvc: ItemName.of([Locale.ja, 'サービスを選択'], [Locale.en, 'Select a service']),
  selectSvcDescription: ItemName.of(
    [Locale.ja, 'サービス内の特定のリソースに対して実行できるアクションを指定します。'],
    [Locale.en, 'Specify what actions can be performed on specific resources in a service.'],
  ),
  selectCondition: ItemName.of([Locale.ja, 'リクエスト条件'], [Locale.en, 'Conditions']),
  selectConditionDescription: ItemName.of(
    [Locale.ja, 'リソースに対するアクションは、これらの条件が満たされた場合にのみ許可または拒否されます。'],
    [Locale.en, 'Actions on resources are allowed or denied only when these conditions are met.'],
  ),
  addConditionTitle: ItemName.of([Locale.ja, 'リクエスト条件を追加'], [Locale.en, 'Add request condition']),
};

const permissionMsgs = {
  setPermission: ItemName.of([Locale.ja, '{0}のアクセス許可を設定'], [Locale.en, 'Set permissions for {0}']),
  actions: ItemName.of([Locale.ja, '{0}アクション'], [Locale.en, '{0} actions']),
};

const logicalConnectiveNames = {
  or: ItemName.of([Locale.ja, 'または'], [Locale.en, 'or']),
  and: ItemName.of([Locale.ja, 'かつ'], [Locale.en, 'and']),
};

const policyMst = {
  attributes,
  logicalConnectiveNames,
  names,
  permissionMsgs,
  permissionNames,
  titleNames,
};

interface PolicyMst {
  schema: Schema;
  record: Record;
}

export type { PolicyMst };
export { policyMst };
